import { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  List,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconVideoPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { qk } from '@/api/queries';
import type { AdminEpisode, AdminProgramme } from '@/api/types';
import { useCan } from '@/auth/useCan';
import { Artwork } from '@/components/Artwork';
import { ReasonTooltip } from '@/components/RoleTooltip';
import { VideoStatusBadge } from '@/components/StatusBadge';
import { VideoPickerModal } from '@/features/videos/VideoPickerModal';
import { VideoPlayback } from '@/features/videos/VideoPlayback';
import { formatAges, formatDuration } from '@/lib/format';
import { imageUrl } from '@/lib/image';
import { Section } from './DetailsTab';
import { useProgrammeFormContext } from './formContext';

/** "Preview before publication": the card and page as a child sees them, plus playback. */
export function PreviewTab({ programme }: { programme: AdminProgramme }) {
  const form = useProgrammeFormContext();
  const v = form.getValues();
  const dirty = form.isDirty();
  const episodes = programme.episodes
    .filter((e) => !e.archived)
    .map((e) => ({ e, season: e.season_number ?? null }));
  const playable = episodes.filter(({ e }) => e.video?.status === 'ready');
  const [playId, setPlayId] = useState<string | null>(playable[0]?.e.id ?? null);
  const playing = playable.find(({ e }) => e.id === playId)?.e;
  const totalDuration = episodes.reduce((sum, { e }) => sum + (e.video?.duration_s ?? 0), 0);
  const meta = [
    v.min_age === '' || v.max_age === '' ? 'Ages not set' : formatAges(v.min_age, v.max_age),
    programme.kind === 'series'
      ? `${episodes.length} episode${episodes.length === 1 ? '' : 's'}`
      : formatDuration(totalDuration || null),
  ];

  return (
    <Stack gap="lg">
      {dirty && (
        <Alert color="sky" variant="light">
          Showing your unsaved changes. Children see the saved version once it’s published.
        </Alert>
      )}

      <Section title="Eligibility" description="What still stops this programme reaching children.">
        {programme.issues.length === 0 ? (
          <Group gap="xs">
            <IconCircleCheck size={18} color="var(--pb-success)" aria-hidden />
            <Text size="sm">
              No blocking issues.{' '}
              {programme.status === 'published'
                ? 'It’s live for children whose settings allow it.'
                : 'It can be approved and published.'}
            </Text>
          </Group>
        ) : (
          <List
            size="sm"
            spacing={4}
            icon={<IconAlertCircle size={16} color="var(--pb-danger)" aria-hidden />}
          >
            {programme.issues.map((i, n) => (
              <List.Item key={`${i.field}-${i.code}-${n}`}>{i.message}</List.Item>
            ))}
          </List>
        )}
        {programme.status !== 'published' && (
          <Text size="xs" c="dimmed">
            Status is {programme.status.replace('_', ' ')} — children can’t see it until it’s
            published. Each child also needs the age range and their safety settings to allow it.
          </Text>
        )}
      </Section>

      <Section title="How children see it">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Card in a row
            </Text>
            <Box w={300} maw="100%">
              <Artwork
                image={v.artwork.thumb ?? v.artwork.landscape}
                title={v.title}
                width="100%"
                ratio={16 / 9}
                alt=""
              />
              <Text fw={700} size="lg" mt={8} lh={1.3}>
                {v.title || 'Untitled'}
              </Text>
              <Group gap={8} mt={4}>
                <Badge variant="outline" color="dark" radius="sm">
                  {meta[0]}
                </Badge>
                <Text size="sm" c="dimmed">
                  {meta[1]}
                </Text>
              </Group>
            </Box>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mt="md">
              Poster
            </Text>
            <Artwork image={v.artwork.poster} title={v.title} width={140} ratio={2 / 3} />
          </Stack>
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Programme page
            </Text>
            <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
              <Box pos="relative">
                <Artwork
                  image={v.artwork.landscape}
                  title={v.title}
                  width="100%"
                  ratio={16 / 9}
                  radius={0}
                />
                {v.artwork.title_art && (
                  <img
                    src={imageUrl(v.artwork.title_art, 600, 200) ?? undefined}
                    alt=""
                    style={{
                      position: 'absolute',
                      left: 16,
                      bottom: 16,
                      width: '45%',
                      maxHeight: '35%',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Box>
              <Stack p="md" gap="xs">
                <Title order={3} size="h2">
                  {v.title || 'Untitled'}
                </Title>
                <Text size="sm" c="dimmed">
                  {meta.join(' · ')}
                  {programme.creator ? ` · ${programme.creator.name}` : ''}
                </Text>
                <Text size="sm">
                  {v.synopsis || v.synopsis_short || (
                    <Text span c="dimmed">
                      No synopsis yet.
                    </Text>
                  )}
                </Text>
                {programme.kind === 'series' && episodes.length > 0 && (
                  <Stack gap={4} mt="xs">
                    {episodes.slice(0, 8).map(({ e, season }) => (
                      <EpisodeLine key={e.id} episode={e} season={season} />
                    ))}
                    {episodes.length > 8 && (
                      <Text size="xs" c="dimmed">
                        and {episodes.length - 8} more
                      </Text>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        </SimpleGrid>
      </Section>

      <Section
        title="Play an episode"
        description="Plays any ready video, whatever the programme’s status."
      >
        {playable.length === 0 ? (
          <Text size="sm" c="dimmed">
            No episode has a ready video yet.
          </Text>
        ) : (
          <Stack gap="sm">
            <Select
              label="Episode"
              data={playable.map(({ e, season }) => ({
                value: e.id,
                label: season ? `S${season} E${e.episode_number ?? '?'} · ${e.title}` : e.title,
              }))}
              value={playId}
              onChange={setPlayId}
              allowDeselect={false}
              maw={420}
            />
            {playing?.video && (
              <VideoPlayback
                key={playing.id}
                video={playing.video}
                label={`Play “${playing.title}”`}
              />
            )}
          </Stack>
        )}
      </Section>

      <PreviewClipSection programme={programme} />
    </Stack>
  );
}

function EpisodeLine({ episode, season }: { episode: AdminEpisode; season: number | null }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed" w={64}>
        {season ? `S${season} E${episode.episode_number ?? '?'}` : 'Extra'}
      </Text>
      <Text size="sm" truncate style={{ flex: 1 }}>
        {episode.title}
      </Text>
      <Text size="xs" c="dimmed">
        {formatDuration(episode.video?.duration_s ?? episode.duration_s)}
      </Text>
    </Group>
  );
}

/** The short preview clip (C21): optional; shown in the app only when approved and ready. */
function PreviewClipSection({ programme }: { programme: AdminProgramme }) {
  const form = useProgrammeFormContext();
  const canApprove = useCan('previewApprove');
  const [picking, setPicking] = useState(false);
  const videoId = form.getValues().preview_video_id;
  const saved = programme.preview.video;
  const video = useQuery({
    queryKey: qk.video(videoId ?? ''),
    queryFn: () => adminApi.getVideo(videoId!),
    enabled: !!videoId && videoId !== saved?.id,
  });
  const current = videoId ? (videoId === saved?.id ? saved : video.data) : null;
  const notReady = !!current && current.status !== 'ready';

  return (
    <Section
      title="Preview clip"
      description="Optional short clip shown in the programme details. Children only see it once an approver approves it and the video is ready."
    >
      {current ? (
        <Group gap="sm">
          <VideoStatusBadge status={current.status} />
          <Text size="sm">{current.title}</Text>
          <Text size="xs" c="dimmed">
            {formatDuration(current.duration_s)}
          </Text>
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          No preview clip.
        </Text>
      )}
      <Group gap="xs">
        <Button
          variant="default"
          leftSection={<IconVideoPlus size={16} />}
          onClick={() => setPicking(true)}
        >
          {current ? 'Change clip' : 'Choose clip'}
        </Button>
        {current && (
          <Button
            variant="subtle"
            color="coral"
            onClick={() => {
              form.setFieldValue('preview_video_id', null);
              form.setFieldValue('preview_approved', false);
            }}
          >
            Remove
          </Button>
        )}
      </Group>
      <ReasonTooltip reason={!canApprove ? 'Only approvers can approve the preview.' : null}>
        <Checkbox
          label="Preview approved for children"
          description={notReady ? 'The clip must be ready before it can be approved.' : undefined}
          disabled={!canApprove || !current}
          {...form.getInputProps('preview_approved', { type: 'checkbox' })}
        />
      </ReasonTooltip>
      {current?.status === 'ready' && (
        <VideoPlayback key={current.id} video={current} label="Play preview" />
      )}
      <VideoPickerModal
        opened={picking}
        onClose={() => setPicking(false)}
        purpose="preview"
        title="Preview clip"
        defaultVideoTitle={`${programme.title} preview`}
        onPick={(v) => {
          form.setFieldValue('preview_video_id', v.id);
          if (v.id !== saved?.id) form.setFieldValue('preview_approved', false);
        }}
      />
    </Section>
  );
}
